import { ReactNode } from 'react';

import classNames from 'classnames';
import Chip, { ChipProps } from '@mui/material/Chip';

import { Box, BoxColors } from '../Box';
import { Counter } from '../Counter';
import { Typography } from '../Typography';
import { EllipsisText } from '../EllipsisText';

import { ChipColor, ChipColorUnion, ChipSize } from './ColoredChip.types';
import { colorScheme, useStyles } from './ColoredChip.styles';

/**
 * Свойства компонента {@link ColoredChip}
 */
export type ColoredChipProps = Omit<ChipProps, 'color' | 'size'> & {
  /**
   * Метка или содержимое чипа.
   */
  label?: string | ReactNode;

  /**
   * Цвет чипа.
   *
   * @defaultValue `ChipColor.gray`
   */
  color?: ChipColorUnion;

  /**
   * Цвет границы чипа.
   */
  borderColor?: BoxColors;

  /**
   * Размер чипа.
   *
   * @defaultValue `'small'`
   */
  size?: keyof typeof ChipSize;

  /**
   * Определяет, должен ли чип занимать всю ширину своего контейнера.
   *
   * @defaultValue `false`
   */
  fullWidth?: boolean;

  /**
   * Определяет, должен ли чип быть отключён.
   *
   * @defaultValue `false`
   */
  disabled?: boolean;

  /**
   * Иконка для отображения в начале чипа.
   */
  startIcon?: ReactNode;

  /**
   * Иконка для отображения в конце чипа.
   */
  endIcon?: ReactNode;

  /**
   * Счётчик для отображения на чипе.
   */
  counter?: number | string;

  /**
   * Определяет, должен ли счётчик быть прилипшим к концу метки чипа.
   *
   * @defaultValue `false`
   */
  counterSticked?: boolean;

  /**
   * Определяет, должен ли счётчик быть подписан.
   *
   * @defaultValue `false`
   */
  signed?: boolean;

  /**
   * Функция для обработки события клика на чип.
   */
  onClick?: () => void;

  /**
   * Ширина чипа (в пикселях как число или в любых CSS-единицах как строка, например `"100%"`).
   */
  width?: string | number;

  /**
   * Определяет, должен ли быть цвет заднего фона белый.
   *
   * @defaultValue `false`
   */
  isBgWhite?: boolean;
};

/**
 * Функция проверяет, является ли переданное значение допустимым цветом чипа.
 *
 * @param str - Значение, которое необходимо проверить.
 * @returns Возвращает true, если значение является допустимым цветом чипа, иначе false.
 */
export const isChipColor = (str: unknown): str is ChipColor =>
  typeof str === 'string' && Object.values<string>(ChipColor).includes(str);

/**
 * Компонент, отображающий цветной чип с необязательными иконками, счётчиком и настраиваемыми размером и цветом.
 *
 * @public
 * @param label - Метка или содержимое чипа.
 * @param color - Цвет чипа.
 * @param size - Размер чипа.
 * @param fullWidth - Определяет, должен ли чип занимать всю ширину своего контейнера.
 * @param width - Ширина чипа (в пикселях как число или в любых CSS-единицах как строка, например `"100%"`).
 * @param disabled - Определяет, должен ли чип быть отключён.
 * @param startIcon - Иконка для отображения в начале чипа.
 * @param endIcon - Иконка для отображения в конце чипа.
 * @param counter - Счётчик для отображения на чипе.
 * @param counterSticked - Определяет, должен ли счётчик быть прилипшим к концу метки чипа.
 * @param signed - Определяет, должен ли счётчик быть подписан.
 * @param onClick - Функция для обработки события клика на чип.
 * @param borderColor - Цвет границы чипа.
 * @param isBgWhite - Определяет, должен ли быть цвет заднего фона белый.
 * @returns Компонент ColoredChip.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link ColoredChipProps}
 */
export const ColoredChip = ({
  label = '',
  fullWidth = false,
  width,
  startIcon,
  endIcon,
  counter,
  size = 'small',
  color = ChipColor.gray,
  borderColor,
  disabled = false,
  counterSticked = false,
  signed = false,
  onClick,
  isBgWhite = false,
  ...rest
}: ColoredChipProps) => {
  const { classes } = useStyles({
    size: ChipSize[size],
    fullWidth,
    color: isChipColor(color) ? ChipColor[color] : ChipColor.gray,
    borderColor,
    disabled,
    width,
    isBgWhite,
  });

  const disabledCounterBgColor = disabled ? 'gray' : undefined;
  const counterBgColor = isBgWhite
    ? (disabledCounterBgColor ?? colorScheme[color].counterBgColor)
    : undefined;

  const resultLabel = (
    <div className={classNames(classes.chipLabel, 'aqa_chip_label')}>
      {startIcon && (
        <Box
          className={classNames(
            classes.icon,
            !!label && classes.startIcon,
            'aqa_chip_start_icon',
          )}
        >
          {startIcon}
        </Box>
      )}
      {label && (
        <Typography
          className={classes.chipLabelTypography}
          variant={size === 'extraSmall' ? 'caption' : 'body3'}
        >
          <EllipsisText rows={1} wordBreak="break-all">
            {label}
          </EllipsisText>
        </Typography>
      )}
      {endIcon && (
        <Box
          className={classNames(
            classes.icon,
            !!label && classes.endIcon,
            'aqa_chip_end_icon',
          )}
        >
          {endIcon}
        </Box>
      )}
      {counter !== undefined && !counterSticked && (
        <Counter
          className={classNames('aqa_chip_counter', classes.counter)}
          counter={counter}
          size={size}
          signed={signed}
          backgroundColor={counterBgColor}
        />
      )}
    </div>
  );

  return (
    <div className={classes.container} data-testid="aqa_chip">
      <Chip
        {...rest}
        className={classNames(
          classes.chip,
          'aqa_chip',
          ...(onClick ? [classes.linkChip] : []),
        )}
        label={resultLabel}
        onClick={onClick}
      />
      {counter != undefined && counterSticked && (
        <Counter
          className={classNames('aqa_chip_counter', classes.counterSticked)}
          counter={counter}
          size={size}
          signed={signed}
          backgroundColor={counterBgColor}
        />
      )}
    </div>
  );
};
